import { sum } from "lodash";
import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { distinctFilter, getSegmentsCrossPointCoords, parseJson } from "utils/dataManipulation";

import { useGetProjectInfo } from "../api/hooks/allHooks";
import { WizardContext } from "../context/Wizard";
import { sortData } from "../utils/dynamicDashboard";
import { formatNumberBasedOnUser } from "../utils/namesManipulation";
import { getCSSVar, resolveCssVar } from "../utils/styling";
import { energyEfficiencyClassConf } from "./AssetOverview";

export const getCrossYear = (data, dataKey) => {
  if (!data?.length > 0) return [];
  const allYears = data?.map((e) => e?.year)?.filter(distinctFilter);
  allYears.sort((a, b) => a - b);
  let crossYears = [];
  for (let index in allYears) {
    index = Number(index);
    const year = allYears[index];
    const nextYear = allYears[index + 1];
    const yearDataItems = data?.filter((e) => e?.year === year && e?.crrem !== undefined && e[dataKey] !== undefined);
    const nextYearDataItems = data?.filter(
      (e) => e?.year === nextYear && e?.crrem !== undefined && e[dataKey] !== undefined
    );

    if (!nextYearDataItems.length > 0 || !yearDataItems.length > 0) continue;
    const yearData = yearDataItems[yearDataItems.length - 1];
    const nextYearData = nextYearDataItems[0];
    const diff = yearData.crrem - yearData[dataKey];
    const nextDiff = nextYearData.crrem - nextYearData[dataKey];
    if (diff >= 0 && nextDiff < 0) {
      crossYears.push([year, nextYear]);
    }
  }

  return crossYears;
};

export const attachValuesToRetrofits = ({ keys, measures, selectedRetrofits }) => {
  return selectedRetrofits?.map((retrofit) => {
    const correspondingRetrofit = measures?.find((measure) => measure.name === retrofit.name);
    if (correspondingRetrofit) {
      const values = {};
      keys?.forEach((key) => {
        values[key] = correspondingRetrofit[key];
      });
      return { ...retrofit, ...values };
    }
    return retrofit;
  });
};

export const calculateStrandingPoint = (data, dataKey) => {
  const strandingPoints = [];
  if (!data?.length > 0) return [];
  const crossYears = getCrossYear(data, dataKey);
  for (const crossYear of crossYears) {
    const [year, nextYear] = crossYear;
    if (!year || !nextYear) continue;
    const yearDataItems = data.filter((e) => e.year === year && e.crrem !== undefined && e[dataKey] !== undefined);
    const nextYearDataItems = data.filter(
      (e) => e.year === nextYear && e.crrem !== undefined && e[dataKey] !== undefined
    );

    if (!nextYearDataItems.length > 0 || !yearDataItems.length > 0) continue;
    const yearData = yearDataItems[yearDataItems.length - 1];
    const nextYearData = nextYearDataItems[0];
    const lineA = [
      [year, yearData[dataKey]],
      [nextYear, nextYearData[dataKey]],
    ];
    const lineB = [
      [year, yearData?.crrem],
      [nextYear, nextYearData?.crrem],
    ];
    strandingPoints.push(getSegmentsCrossPointCoords(lineA, lineB));
  }
  return strandingPoints;
};

export const getRetrofitYCoords = (retrofits, data, dataKey = "amount", baselineKey = "current") => {
  let retrofitYCoords = [];
  retrofits.forEach((retrofit, index) => {
    const sumOfPreviousRetros = sum(retrofits.slice(0, index).map((e) => e[dataKey]));
    const retrofitYearData = data?.find((e) => e.year === retrofit.year);
    if (!retrofitYearData) return;
    retrofitYCoords.push([
      retrofitYearData?.[baselineKey] - (sumOfPreviousRetros || 0),
      retrofitYearData?.[baselineKey] - (sumOfPreviousRetros || 0) - retrofit[dataKey],
    ]);
  });
  return retrofitYCoords;
};

export const addRetrofitsToData = (retrofits, data) => {
  if (!retrofits?.length > 0) return data;
  retrofits.sort((a, b) => a.year - b.year);
  const retrofitYCoords = getRetrofitYCoords(retrofits, data);
  const primaryRetrofitYCoords = getRetrofitYCoords(retrofits, data, "primaryModernized", "baselinePrimary");
  if (!retrofitYCoords?.length > 0) return data;
  const newData = data.map((e) => {
    const modernized = e.current - sum(retrofits?.filter((retro) => e.year > retro.year)?.map((retro) => retro.amount));
    const primaryModernized =
      e.baselinePrimary -
      sum(retrofits?.filter((retro) => e.year > retro.year)?.map((retro) => retro.primaryModernized));
    return { ...e, modernized: Math.max(modernized || e.current, 0), primaryModernized };
  });
  retrofitYCoords.forEach((_, index) => {
    newData.push({
      primaryRetrofit: Math.max(primaryRetrofitYCoords[index][0], 0),
      retrofit: Math.max(retrofitYCoords[index][0], 0),
      year: retrofits[index].year,
    });
    newData.push({
      modernized: Math.max(retrofitYCoords[index][1], 0),
      primaryModernized: Math.max(primaryRetrofitYCoords[index][1], 0),
      primaryRetrofit: Math.max(primaryRetrofitYCoords[index][1] + 0.5, 0),
      retrofit: Math.max(retrofitYCoords[index][1] + 0.5, 0),
      year: retrofits[index].year,
    });
  });

  newData.sort((a, b) => a.year - b.year);
  return newData;
};

export const getNGF = (consumption, grossArea, netArea) => {
  return (consumption * grossArea) / netArea;
};

const getIthInflatedValue = (current, iteration, inflationRatePercentage) => {
  return Math.pow(1 + inflationRatePercentage / 100, iteration) * current;
};

const getIthLoanRepaymentValue = (loan, repaymentTime, iteration) => {
  if (iteration < 0 || iteration > repaymentTime - 1) return 0;
  return loan / repaymentTime;
};

const getIthInterestedValue = (current, totalIterations, iteration, interestRatePercentage) => {
  if (iteration < 0 || iteration > totalIterations - 1) return 0;
  return current * (interestRatePercentage / 100);
};

export const getDefaultLoanRepaymentTime = () => {
  return 10;
};

export const getSingleMeasureFinancialValuesTimeSeries = ({ endYear, initialValues, startYear }) => {
  const {
    foreignCapital,
    inflationRatePercentage,
    initialCostsSavings,
    initialEnergySaving,
    initialMaintenanceCosts,
    initialOtherSavings,
    initialServicesCosts,
    interestRatePercentage,
    investments,
    loanRepaymentTime,
    ownCapital,
    subsidies,
  } = initialValues;

  const output = [];
  const startYearInputsValues = { foreignCapital, investments, ownCapital, subsidies };
  const OtherYearsInputValues = { foreignCapital: 0, investments: 0, ownCapital: 0, subsidies: 0 };

  for (let year = startYear; year <= endYear; year++) {
    let timeSeriesItem;
    const iteration = year - startYear;

    timeSeriesItem = {
      costSavings: getIthInflatedValue(initialCostsSavings, iteration, inflationRatePercentage),
      energySavings: getIthInflatedValue(initialEnergySaving, iteration, inflationRatePercentage),
      foreignCapitalCost: -getIthInterestedValue(foreignCapital, loanRepaymentTime, iteration, interestRatePercentage),
      loanRepayment: -getIthLoanRepaymentValue(foreignCapital, loanRepaymentTime, iteration),
      maintenanceCosts: -getIthInflatedValue(initialMaintenanceCosts, iteration, inflationRatePercentage),
      otherSavings: getIthInflatedValue(initialOtherSavings, iteration, inflationRatePercentage),
      servicesCosts: -getIthInflatedValue(initialServicesCosts, iteration, inflationRatePercentage),
      year,
      ...(iteration === 0 ? startYearInputsValues : OtherYearsInputValues),
    };

    timeSeriesItem.cashFlow =
      timeSeriesItem["energySavings"] +
      timeSeriesItem["foreignCapitalCost"] +
      timeSeriesItem["loanRepayment"] +
      timeSeriesItem["maintenanceCosts"] +
      timeSeriesItem["otherSavings"] +
      timeSeriesItem["servicesCosts"] -
      timeSeriesItem["ownCapital"];

    output.push(timeSeriesItem);
  }
  return output;
};

export const useGetEnergyEfficiencyClassesTimelineData = () => {
  const { projectId } = useParams();
  const { data, setCrremScenario, yearRange } = useContext(WizardContext);
  const { data: projectInfo } = useGetProjectInfo(projectId, {});

  useEffect(() => {
    const crremScenarioList = parseJson(projectInfo?.[0]?.crrem_scenario_list) ?? [];
    setCrremScenario((prev) => prev ?? crremScenarioList?.[0]);
  }, [projectInfo, setCrremScenario]);

  const maxConsumption = Math.max(...data.map((yearData) => yearData.primaryModernized ?? 0));

  const { energyClasses, labels, thresholds } = useGetEnergyEfficiencyClasses();

  const dataWithEnergyLabels = data?.map((yearData) => {
    const energyValues = {};

    energyClasses.forEach((energyClass, i) => {
      const previousClassValue = energyClasses[i - 1]?.max;
      const currentClassValue =
        energyClass.max !== Infinity ? energyClass.max : Math.max(energyClasses[i - 1].max * 1.2, maxConsumption);
      const energyClassDiff = i === 0 ? energyClass.max : currentClassValue - previousClassValue;

      energyValues[energyClass.rating] = energyClassDiff;
    });
    return { ...yearData, ...energyValues };
  });

  const dataForChart = dataWithEnergyLabels?.filter(
    (yearData) => yearData.year <= yearRange[1] && yearData.year >= yearRange[0]
  );

  return { dataForChart, energyClasses, labels, maxConsumption, thresholds };
};

export const getEnergyEfficiencyClassesTimelineChartOption = (
  dataForChart,
  energyClasses,
  labels,
  maxConsumption,
  thresholds
) => {
  const sortedYears = sortData({ data: dataForChart, dataKey: "year", decsending: false })
    .map((row) => row.year)
    .filter(distinctFilter);

  const option = {
    animation: true,
    dataZoom: [
      {
        right: 0,
        type: "slider",
        width: 16,
        yAxisIndex: 0,
      },
    ],
    grid: {
      bottom: 70,
      containLabel: true,
      left: 20,
      right: 10,
      top: 55,
    },
    legend: {
      bottom: 0,
      left: 10,
      orient: "horizontal",
      right: 40,
      textStyle: {
        fontFamily: getCSSVar("--font-primary"),
        fontSize: "1.3rem",
        fontWeight: 600,
      },
    },

    series: [
      ...energyClasses.map((energyClass) => ({
        areaStyle: { color: resolveCssVar(energyClass.bgColor), opacity: 0.5 },
        color: resolveCssVar(energyClass.bgColor),
        data: sortedYears.map((_year, index) => [index, dataForChart[0][energyClass.rating]]),
        emphasis: {
          disabled: true,
        },
        label: {
          color: getCSSVar("--color-purpose-modernization-charts-energy-class-label"),
          fontSize: 10,
          fontWeight: "bold",
          formatter: energyClass.rating,
          position: [-10, 0],
          show: true,
        },
        lineStyle: {
          color: getCSSVar("--color-purpose-modernization-charts-energy-class-band-border"),
        },
        name: energyClass.rating,
        silent: true,
        stack: "total",
        stackStrategy: "all",
        symbol: "none",
        type: "line",
      })),
      {
        color: getCSSVar("--color-purpose-modernization-charts-retrofit"),
        data: dataForChart.map((d) => {
          const index = sortedYears?.findIndex((year) => year === d.year);
          return [index, d.primaryRetrofit ?? null];
        }),
        lineStyle: { color: getCSSVar("--color-purpose-modernization-charts-retrofit"), width: 2 },
        name: "Retrofit",
        smooth: false,
        symbol: "none",
        type: "line",
      },
      {
        color: getCSSVar("--color-purpose-modernization-charts-modernized"),
        data: dataForChart.map((d) => {
          const index = sortedYears?.findIndex((year) => year === d.year);
          return [index, d.primaryModernized ?? null];
        }),
        lineStyle: { color: getCSSVar("--color-purpose-modernization-charts-modernized"), width: 2 },
        name: "Primary Energy",
        smooth: false,
        symbol: "none",
        type: "line",
      },
    ],
    tooltip: {
      formatter: (params) => {
        const year = Number(params?.[0]?.axisValue);
        const filtered = params
          .filter((p) => {
            return p.data?.[1] !== null && p.data?.[1] !== undefined && p.seriesName !== "Retrofit";
          })
          .map((p) => {
            const index = labels?.findIndex((label) => label === p.seriesName);
            if (index === -1) return;
            const value = thresholds[index + 1];
            if (value) return `${p.marker} ${p.seriesName}: ${formatNumberBasedOnUser(parseFloat(value))}`;
          })
          .join("<br/>");
        return `<h1>${year}</h1>
            ${filtered} `;
      },
      trigger: "axis",
    },
    xAxis: [
      {
        axisLabel: {
          fontFamily: getCSSVar("--font-heading"),
          fontSize: 12,
          fontWeight: 400,
          rotate: 270,
        },
        data: sortedYears,
        type: "category",
      },
    ],
    yAxis: [
      {
        axisLabel: {
          fontFamily: getCSSVar("--font-heading"),
          fontSize: 12,
          fontWeight: 400,
        },
        max: Math.ceil(Math.max(maxConsumption, thresholds[thresholds.length - 1]) / 10) * 10,
        min: 0,
        name: "kWh/m² NGF",
        nameLocation: "end",
        scale: false,
        type: "value",
      },
    ],
  };

  return option;
};

export const useGetEnergyEfficiencyClasses = () => {
  const { epcInfo } = useContext(WizardContext);

  const classification = epcInfo?.classification;
  let maxEnergyValue = 600;

  let thresholds = [];
  let labels = [];
  let minEnergyValue;
  const labelsMapping = {
    a: "A",
    a_plus: "A+",
    b: "B",
    c: "C",
    d: "D",
    e: "E",
    f: "F",
    g: "G",
    h: "H",
  };

  if (classification) {
    const activeClassesNonZero = Object.keys(labelsMapping).filter(
      (className) => classification[`class_${className}_min`]
    );
    const lastActiveClass = activeClassesNonZero[activeClassesNonZero?.length - 1];
    maxEnergyValue = classification[`class_${lastActiveClass}_min`] * 1.2;
    for (const className in labelsMapping) {
      const isLastActiveClass = className === lastActiveClass;
      const classMin = classification[`class_${className}_min`];
      const classMax = classification[`class_${className}_max`];

      if (!classMin && !classMax) continue;
      if (minEnergyValue === undefined) {
        minEnergyValue = classMin;
        thresholds.push(classMin);
      }
      if (classMax && !isLastActiveClass) {
        labels.push(labelsMapping[className]);
        thresholds.push(classMax);
      }
      if (classMax && isLastActiveClass) {
        labels.push(labelsMapping[className]);
      }
      if (classMin && !classMax) {
        labels.push(labelsMapping[className]);
      }
      if (isLastActiveClass) break;
    }
    thresholds.push(maxEnergyValue);
  }

  const energyClasses = energyEfficiencyClassConf
    .filter((energyClass) => labels?.includes(energyClass.rating))
    .map((energyClass) => {
      const classIndex = labels?.findIndex((label) => energyClass.rating === label);
      return {
        ...energyClass,
        max: thresholds?.[classIndex + 1],
      };
    });

  return { energyClasses, labels, thresholds };
};
